/**
 * Owns the Step2 market-data keyword controls: hot keywords, ASIN expansion,
 * and manual additions. The Step2 entry composes this controller so request
 * details and transient button state do not stay mixed with table rendering.
 */
export function createMarketDataKeywordController({
  select,
  parseAsinsFromText,
  getErrorMessage,
  fetchMarketDataKeywords,
  getMarketDataKeywords,
  extractKeywordText,
  setMarketDataKeywords,
  schedule = setTimeout,
}) {
  function setMarketDataStatus(message, duration = 3000) {
    const status = select('#st-mcp-status');
    if (!status) return;
    status.textContent = message;
    status.hidden = !message;
    if (message && duration > 0) {
      schedule(() => {
        status.hidden = true;
      }, duration);
    }
  }

  function setLoading(loading) {
    const indicator = select('#st-loading-indicator');
    if (indicator) indicator.hidden = !loading;
  }

  function mergeKeywords(keywords) {
    const current = getMarketDataKeywords();
    const existing = new Set(current.map(extractKeywordText));
    const unique = (Array.isArray(keywords) ? keywords : [])
      .filter((keyword) => !existing.has(extractKeywordText(keyword)));
    setMarketDataKeywords([...current, ...unique]);
    return unique;
  }

  async function fetchHotKeywords() {
    const button = select('#fetch-hot-keywords-btn');
    const marketplace = select('#product-marketplace')?.value || 'US';
    if (button) button.disabled = true;
    setMarketDataStatus('获取中...');
    setLoading(true);

    try {
      const data = await fetchMarketDataKeywords({
        action: 'hot',
        marketplace,
        page: 1,
        seedKeywords: getMarketDataKeywords().slice(0, 5).map(extractKeywordText).filter(Boolean),
      });
      const keywords = Array.isArray(data?.keywords) ? data.keywords : [];
      mergeKeywords(keywords);
      setMarketDataStatus(keywords.length > 0 ? `已获取 ${keywords.length} 个热搜词` : '未获取到数据');
    } catch (error) {
      setMarketDataStatus(`失败: ${getErrorMessage(error)}`);
    } finally {
      if (button) button.disabled = false;
      setLoading(false);
    }
  }

  async function expandKeywordsFromAsin() {
    const button = select('#expand-keywords-btn');
    const marketplace = select('#product-marketplace')?.value || 'US';
    const asins = parseAsinsFromText(select('#product-asins')?.value || '');
    if (asins.length === 0) {
      setMarketDataStatus('请先输入竞品 ASIN', 2000);
      return;
    }

    if (button) button.disabled = true;
    setMarketDataStatus('拓展中...');
    setLoading(true);

    try {
      const data = await fetchMarketDataKeywords({ action: 'expand', marketplace, asins });
      const unique = mergeKeywords(data?.keywords);
      setMarketDataStatus(unique.length > 0 ? `新增 ${unique.length} 个拓展词` : '暂无新增拓展词');
    } catch (error) {
      setMarketDataStatus(`失败: ${getErrorMessage(error)}`);
    } finally {
      if (button) button.disabled = false;
      setLoading(false);
    }
  }

  function toggleManualKeywordInput() {
    const area = select('#manual-keyword-input-area');
    if (!area) return;
    const show = area.hidden;
    area.hidden = !show;
    if (show) schedule(() => select('#manual-keyword-input')?.focus(), 0);
  }

  function hideManualKeywordInput() {
    const area = select('#manual-keyword-input-area');
    if (area) area.hidden = true;
  }

  function commitManualKeywords() {
    const input = select('#manual-keyword-input');
    if (!input) return;
    const raw = String(input.value || '').trim();
    if (!raw) {
      setMarketDataStatus('请输入至少一个关键词', 2000);
      return;
    }

    const phrases = raw
      .split(/[\n,，]+/)
      .map((value) => value.trim())
      .filter((value) => value.length > 0 && value.length < 80);
    if (phrases.length === 0) {
      setMarketDataStatus('未识别到有效关键词', 2000);
      return;
    }

    const current = getMarketDataKeywords();
    const existing = new Set(current.map((keyword) => extractKeywordText(keyword).toLowerCase()));
    const unique = phrases
      .filter((phrase) => !existing.has(phrase.toLowerCase()))
      .map((phrase) => ({ 关键词: phrase, '30天搜索量': 0, source: 'manual' }));
    if (unique.length === 0) {
      setMarketDataStatus('这些关键词已存在', 2000);
      return;
    }

    setMarketDataKeywords([...current, ...unique]);
    input.value = '';
    hideManualKeywordInput();
    setMarketDataStatus(`已手动添加 ${unique.length} 个关键词`);
  }

  return {
    fetchHotKeywords,
    expandKeywordsFromAsin,
    toggleManualKeywordInput,
    hideManualKeywordInput,
    commitManualKeywords,
  };
}
